import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { authService } from '@/features/auth/services/authService';
import { useAuthStore } from '@/features/auth/store/authStore';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { BackendStatus } from '@/components/shared/BackendStatus';
import { Mail, Lock, Sparkles, Users, Trophy, Gamepad2 } from 'lucide-react';
import { useState } from 'react';

import { API_BASE_URL } from '@/lib/api/apiClient';
const API_URL = API_BASE_URL || '';

const ruqaaFont = { fontFamily: "'Aref Ruqaa', 'Amiri', serif" };
const arabicFont = { fontFamily: "'Almarai', 'Cairo', sans-serif" };

const loginSchema = z.object({
  email: z.string().min(1, 'البريد أو اسم المستخدم مطلوب'),
  password: z.string().min(1, 'كلمة المرور مطلوبة'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const PlayerLoginPage = () => {
  const { login } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await authService.login(data);
      const userRole = String(response.user.role || '').trim();
      const hasAdminRole = userRole === 'Admin' || userRole === 'SuperAdmin' || userRole === '3' || userRole === '4';
      
      login(response.token, response.user);
      await new Promise(resolve => setTimeout(resolve, 100));
      
      toast.success('تم تسجيل الدخول بنجاح!');
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (hasAdminRole) {
        window.location.href = '/admin';
      } else {
        window.location.href = '/player';
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'فشل تسجيل الدخول';
      toast.error(errorMessage, { duration: 5000 });
    }
  };

  return (
    <div className="min-h-screen flex" style={arabicFont}>
      {/* Green Section - LEFT */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-green-600 via-green-700 to-green-800 flex-col justify-center items-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 text-center text-white">
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-white/20 rounded-full blur-3xl scale-110" />
            <img 
              src={API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg'}
              alt="اعصر مخك"
              className="relative w-56 h-56 mx-auto object-contain"
              style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))', animation: 'float 4s ease-in-out infinite' }}
            />
          </div>

          <h2 className="text-5xl md:text-6xl mb-4" style={{ ...ruqaaFont, fontWeight: 700, textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
            اعصر مخك
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-md">لعبة المعلومات الأولى للتجمعات العائلية والأصدقاء</p>

          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <Users className="w-6 h-6 text-yellow-300" /><span className="font-bold">فريقين متنافسين</span>
            </div>
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <Trophy className="w-6 h-6 text-yellow-300" /><span className="font-bold">تحدي مثير وممتع</span>
            </div>
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <Sparkles className="w-6 h-6 text-yellow-300" /><span className="font-bold">6 فئات - 36 سؤال</span>
            </div>
          </div>

          <div className="flex gap-8 justify-center mt-10">
            <div className="text-center"><p className="text-3xl font-black text-yellow-300">+10K</p><p className="text-sm text-white/80">لاعب</p></div>
            <div className="text-center"><p className="text-3xl font-black text-yellow-300">+50K</p><p className="text-sm text-white/80">سؤال</p></div>
            <div className="text-center"><p className="text-3xl font-black text-yellow-300">+100</p><p className="text-sm text-white/80">فئة</p></div>
          </div>
        </div>

        <style>{`@keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-15px); } }`}</style>
      </div>

      {/* Form Section - RIGHT */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-8 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-green-100 rounded-full translate-x-1/2 -translate-y-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-100 rounded-full -translate-x-1/4 translate-y-1/4 opacity-50" />
        
        <BackendStatus />

        <div className="text-center mb-8 relative z-10">
          <Link to="/">
            <img 
              src={API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg'}
              alt="اعصر مخك"
              className="w-28 h-28 mx-auto mb-4 object-contain lg:hidden"
              style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }}
            />
          </Link>
          <h1 
            className="text-5xl md:text-6xl text-gray-800 mb-2"
            style={{ ...ruqaaFont, fontWeight: 700 }}
          >
            اعصر مخك
          </h1>
          <p className="text-gray-500 text-lg">مرحباً بك مجدداً!</p>
        </div>

        <div className="w-full max-w-md relative z-10">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="relative">
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                <Mail className="w-5 h-5" />
              </div>
              <input
                type="text"
                placeholder="البريد الإلكتروني أو اسم المستخدم"
                {...register('email')}
                className={`w-full pr-12 pl-4 py-4 text-lg border-2 rounded-2xl outline-none transition-all
                  ${errors.email ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-green-500'}
                  bg-gray-50 focus:bg-white`}
                style={arabicFont}
              />
              {errors.email && <p className="text-red-500 text-sm mt-1 mr-2">{errors.email.message}</p>}
            </div>

            <div className="relative">
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                <Lock className="w-5 h-5" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="كلمة المرور"
                {...register('password')}
                className="w-full pr-12 pl-12 py-4 text-lg border-2 rounded-2xl outline-none transition-all border-gray-200 focus:border-green-500 bg-gray-50 focus:bg-white"
                style={arabicFont}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>

            <div className="text-left">
              <Link to="/forgot-password" className="text-green-600 hover:text-green-700 text-sm font-medium">
                نسيت كلمة المرور؟
              </Link>
            </div>

            <Button 
              type="submit" 
              className="w-full py-6 text-xl font-bold rounded-2xl bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoadingSpinner size="sm" /> : <><Gamepad2 className="w-6 h-6 ml-2" />تسجيل الدخول</>}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
              <div className="relative flex justify-center"><span className="bg-white px-4 text-gray-400 text-sm">أو</span></div>
            </div>

            <div className="text-center">
              <p className="text-gray-600 mb-3">ليس لديك حساب؟</p>
              <Link to="/register">
                <Button type="button" variant="outline" className="w-full py-5 text-lg font-bold rounded-2xl border-2 border-green-500 text-green-600 hover:bg-green-50 transition-all">
                  <Sparkles className="w-5 h-5 ml-2" />إنشاء حساب جديد - لعبة مجانية!
                </Button>
              </Link>
            </div>
          </form>

          <div className="mt-8 p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-2xl border-2 border-yellow-200">
            <div className="flex items-center justify-center gap-3">
              <span className="text-2xl">🎁</span>
              <p className="text-yellow-800 font-bold">سجّل الآن واحصل على لعبة مجانية!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerLoginPage;
